import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { CommonModule } from '@angular/common'
import { Component, inject, signal, Signal } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatTableModule } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button'
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet'
import { Client } from '../../../control-panel.service'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { HttpErrorResponse } from '@angular/common/http'
import { GenericErrorComponent } from '../../../../../simple-bottom-sheets/generic-error.component'
import { Category, ConciergeEditProfileService } from './edit-profile.service'
import { NgxMaskDirective } from 'ngx-mask'
import { ConciergeModalSucessComponent } from '../../../modal-sucess/modal-success.component'
import { LoadingPageComponent } from '@monorepo-channels/components/ui-pm'

interface FieldState {
	focused: Signal<boolean>
	editing: Signal<boolean>
	setFocused: (value: boolean) => void
	setEditing: (value: boolean) => void
}

interface NewCategory {
	categoryId: string
	digits: number
	name: string
}

@Component({
	standalone: true,
	selector: 'feature-pm-concierge-edit-profile-content',
	templateUrl: './edit-profile.component.html',
	styleUrls: ['./edit-profile.component.scss'],
	imports: [
		MatTableModule,
		CommonModule,
		MatIconModule,
		MatButtonModule,
		MatBottomSheetModule,
		ReactiveFormsModule,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		NgxMaskDirective,
		LoadingPageComponent,
	],
})
export class ConciergeEditProfileComponent {
	private fb = inject(FormBuilder)
	private bottomSheet = inject(MatBottomSheet)
	public displayedColumns: string[] = ['number', 'type']
	public client = inject<Client>(MAT_DIALOG_DATA)
	private dialogRef = inject(MatDialogRef<ConciergeEditProfileComponent>)
	private dialog = inject(MatDialog)
	isLoading = false

	isNewCardAdd = signal(false)
	newCard?: NewCategory[] = []
	private editProfileService = inject(ConciergeEditProfileService)

	private fieldStates = new Map<string, FieldState>()

	private emailValidator = Validators.pattern(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)

	public userForm = this.fb.nonNullable.group({
		name: [{ value: '', disabled: false }, [Validators.required]],
		email: [{ value: '', disabled: false }, [Validators.required, this.emailValidator]],
		phone: [{ value: '', disabled: false }, [Validators.required]],
		cpf: [{ value: '', disabled: true }],
	})

	constructor() {
		;['name', 'email', 'phone', 'cpf'].forEach(fieldName => {
			this.initializeFieldState(fieldName)
		})
		if (this.client) {
			this.userForm.patchValue({
				name: `${this.client.name} ${this.client.lastName}`,
				email: this.client.email,
				phone: this.client?.phone ?? '',
				cpf: this.client.cpf,
			})
		}
	}

	private initializeFieldState(fieldName: string) {
		const focused = signal(false)
		const editing = signal(false)

		this.fieldStates.set(fieldName, {
			focused,
			editing,
			setFocused: (value: boolean) => focused.set(value),
			setEditing: (value: boolean) => editing.set(value),
		})
	}

	get allCards(): Category[] {
		const allCards = [...(this.client.categories || []), ...(this.newCard || [])].map(card => ({
			...card,
			isNew: Object.keys(card).length === 3,
		}))
		return allCards
	}

	// Review
	addCard(newCardDigits: string) {
		if (newCardDigits.toString().length === 6) {
			this.editProfileService.getRange(Number(newCardDigits)).subscribe({
				next: (response: Category) => {
					const newCardData = {
						digits: Number(newCardDigits),
						name: response.name,
						categoryId: response._id,
					}

					this.newCard?.push({
						categoryId: newCardData.categoryId!,
						digits: newCardData.digits,
						name: newCardData.name!,
					})
					this.newCard = [...this.newCard!]
					this.isNewCardAdd.set(true)
				},
				error: (err: HttpErrorResponse) => {
					const errorMessage = err.error?.message || 'Erro desconhecido'
					this.bottomSheet.open(GenericErrorComponent, {
						data: {
							error: errorMessage,
							title: 'Erro ao buscar cartão',
							btnMessage: 'Fechar',
						},
					})
				},
			})
		}
	}

	closeModal() {
		this.userForm.reset()
		this.dialogRef.close()
	}

	getFieldState(fieldName: string): FieldState {
		return this.fieldStates.get(fieldName)!
	}

	save() {
		if (this.userForm.invalid) return
		this.isLoading = true

		const categoriesToUpdate = this.newCard?.map(card => card.digits) || []
		const clientToUpdate = {
			name: this.userForm.value.name,
			phone: this.userForm.value.phone,
			categories: categoriesToUpdate,
		}
		this.editProfileService.updateClient(this.client._id, clientToUpdate).subscribe({
			next: () => {
				this.dialog.open(ConciergeModalSucessComponent, { data: 'Suas alterações foram salvas!' })
				this.dialogRef.close('updated')
				this.isLoading = false
			},
			error: (err: HttpErrorResponse) => {
				const errorMessage = err.error?.message || 'Erro desconhecido'
				this.bottomSheet.open(GenericErrorComponent, {
					data: {
						error: errorMessage,
						title: 'Erro ao editar cliente',
						btnMessage: 'Fechar',
					},
				})
				this.isLoading = false
			},
		})
	}
}
